import React, { useState, useEffect } from "react";
import { Dropdown, Offcanvas, Table } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import { toTimestamp } from "../utilities/functions";
import { PropagateLoader } from "react-spinners";
import {
  getNotificationTarget,
  getNotificationRoute,
} from "../utilities/notificationLinks";

interface NotificationsProps {
  id_notification: number;
  id_user: number;
  type: string;
  message: string;
  severity: number;
  timestamp: string;
  state: number;
  username?: string;
  email?: string;
}

const parseNotificationMessage = (message: string) => {
  const text = String(message || "").trim();
  const adhesionMatch = text.match(
    /^Nouvelle adhesion AIDA\s+(OEM|fournisseur)\s*:\s*(.*?)\s*-\s*compte\s*(.*?)\s*\((.*?)\)\s*-\s*utilisateur\s*#\s*(\d+)\.?$/i,
  );

  if (adhesionMatch) {
    return {
      kind: adhesionMatch[1],
      company: adhesionMatch[2],
      account: adhesionMatch[3],
      email: adhesionMatch[4],
      userId: adhesionMatch[5],
      summary: adhesionMatch[2],
      raw: text,
    };
  }

  const userMatch = text.match(/utilisateur\s*#\s*(\d+)/i);

  return {
    kind: "",
    company: text.split(" - ")[0] || text,
    account: "",
    email: "",
    userId: userMatch?.[1] || "",
    summary: text,
    raw: text,
  };
};

export function Notifications() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { id_notification } = useParams<{ id_notification?: string }>();
  const { translate } = useTranslate();
  const navigate = useNavigate();

  const id_user = localStorage.getItem("userid");

  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [list_Notifications, setNotifications] = useState<NotificationsProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedNotification, setSelectedNotification] =
    useState<NotificationsProps | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);

  const [column, setSortColum] = useState("id_notification");
  const [sort, setSort] = useState("DESC");
  const [search, setSearch] = useState(id_notification || "");
  const [type, setType] = useState(0);
  const [typeSearch, setTypeSearch] = useState(translate("notification") + " ID");

  const [selectedColumns, setSelectedColumns] = useState({
    id_notification: true,
    type: true,
    message: true,
    severity: false,
    state: true,
    timestamp: true,
    username: false,
    email: false,
  });

  const visibleColumnCount =
    2 + Object.values(selectedColumns).filter(Boolean).length;

  const getNotifications = async (
    limitValue: number,
    pageValue: number,
    searchValue: string,
    typeValue: number,
    columnValue: string,
    sortValue: string
  ) => {
    try {
      setLoading(true);

      const bodyData = JSON.stringify({
        limitValue,
        currentPage: pageValue,
        search: searchValue,
        type: typeValue,
        id_user, // 🔥 SEULEMENT ça
        column: columnValue, // 🔥 UN SEUL champ
        sort: sortValue,
      });

      // ===== TOTAL =====
      const totalPagesResponse = await fetch(
        `${backendUrl}/api/notification/totalpage`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: bodyData,
        }
      );

      const totalPagesJson = await totalPagesResponse.json();
      const totalCount = totalPagesJson?.[0]?.count || 0;

      setTotal(totalCount);
      setPageCount(Math.ceil(totalCount / limitValue));

      // ===== LIST =====
      const response = await fetch(
        `${backendUrl}/api/notification/search`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: bodyData,
        }
      );

      const data = await response.json();

      setNotifications(Array.isArray(data) ? data : []);
      setLimit(limitValue);
      setCurrentPage(pageValue);

    } catch (error) {
      console.error(error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markNotificationAsRead = async (idNotification: number) => {
    try {
      await fetch(`${backendUrl}/api/notification/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_notification: idNotification }),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const refreshNotificationState = (idNotification: number) => {
    setNotifications((prev) =>
      prev.map((item) =>
        item.id_notification === idNotification ? { ...item, state: 0 } : item,
      ),
    );
  };

  const handleOpenNotification = async (notification: NotificationsProps) => {
    await markNotificationAsRead(notification.id_notification);
    refreshNotificationState(notification.id_notification);
    setSelectedNotification({ ...notification, state: 0 });
    setDetailDrawerOpen(true);
  };

  const handleGoToNotificationTarget = async (notification: NotificationsProps) => {
    await markNotificationAsRead(notification.id_notification);
    refreshNotificationState(notification.id_notification);
    navigate(getNotificationRoute(notification), {
      state: { canEditCompany: true },
    });
  };
  useEffect(() => {
    getNotifications(limit, currentPage, search, type, column, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageClick = async (data: any) => {
    const nextPage = data.selected + 1;
    await getNotifications(limit, nextPage, search, type, column, sort);
    window.scrollTo(0, 0);
  };

  const handleSelectChange = async (event: any) => {
    const newLimit = parseInt(event.target.value, 10);
    await getNotifications(newLimit, 1, search, type, column, sort);
    window.scrollTo(0, 0);
  };

  const handleColumnChange = (columnName: keyof typeof selectedColumns) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  const handleTypeSearch = (event: any) => {
    const selectedValue = event.target.textContent;

    switch (selectedValue) {
      case translate("notification") + " ID":
        setType(0);
        break;
      case translate("Type"):
        setType(1);
        break;
      case translate("Message"):
      case translate("Details"):
        setType(2);
        break;
      case translate("User"):
        setType(3);
        break;
      default:
        setType(0);
        break;
    }

    setTypeSearch(selectedValue);
  };

  const handleAdvancedSearch = async (event: any) => {
    const newValue = event.target.value;
    setSearch(newValue);
    await getNotifications(limit, 1, newValue, type, column, sort);
  };

  const handleSortingColum = async (currentColumn: string) => {
    const nextSort = sort === "ASC" ? "DESC" : "ASC";
    setSortColum(currentColumn);
    setSort(nextSort);
    await getNotifications(limit, 1, search, type, currentColumn, nextSort);
  };

  return (
    <>
      <div className="row">
        <div className="col-md-6 col-sm-12">
          <h4>
            <span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-bell"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
            </span>{" "}
            {translate("notifications")} ({total})
          </h4>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4" style={{ margin: "0px 0px 10px 0px", padding: "10px" }}>
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle
                variant="link"
                id="dropdown-basic"
                className="notification-filter-toggle"
              >
                <i className="fas fa-chevron-down" style={{ fontSize: "20px" }}></i>
              </Dropdown.Toggle>
              <Dropdown.Menu onClick={handleTypeSearch}>
                <Dropdown.Item>{translate("notification") + " ID"}</Dropdown.Item>
                <Dropdown.Item>{translate("Type")}</Dropdown.Item>
                <Dropdown.Item>{translate("Message")}</Dropdown.Item>
                <Dropdown.Item>{translate("User")}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              placeholder={`${typeSearch}`}
              value={search}
              onChange={handleAdvancedSearch}
              className="form-control"
            />
          </div>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length" id="DataTables_Table_0_length">
            <label style={{ marginBottom: "0" }}>
              {translate("Show")}
              <select
                className="custom-select custom-select-sm form-control form-control-sm ml-2"
                style={{ width: "66px" }}
                value={limit}
                onChange={handleSelectChange}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>

          <Dropdown>
            <Dropdown.Toggle variant="" id="dropdown-columns" title="Colonnes dʼaffichage">
              <i className="las la-eye"></i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {Object.keys(selectedColumns).map((key) => (
                <Dropdown.Item
                  key={key}
                  as="button"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedColumns[key as keyof typeof selectedColumns]}
                    onChange={() => handleColumnChange(key as keyof typeof selectedColumns)}
                  />
                  <span style={{ marginLeft: "10px" }}>{translate(key)}</span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <div className="table-responsive notification-table-wrap">
        <div className="notification-table-inner">
          <Table className="dataTable notification-table">
            <thead className="bg-white text-uppercase">
              <tr className="ligth ligth-data">
                <th>
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="checkbox" />
                  </div>
                </th>

                {selectedColumns.id_notification && (
                  <th className="sorting" onClick={() => handleSortingColum("id_notification")}>
                    {translate("Code")}
                  </th>
                )}

                {selectedColumns.type && (
                  <th className="sorting" onClick={() => handleSortingColum("type")}>
                    {translate("Type")}
                  </th>
                )}

                {selectedColumns.message && (
                  <th className="sorting" onClick={() => handleSortingColum("message")}>
                    {translate("Message")}
                  </th>
                )}

                {selectedColumns.severity && (
                  <th className="sorting" onClick={() => handleSortingColum("severity")}>
                    {translate("Severity")}
                  </th>
                )}

                {selectedColumns.state && (
                  <th className="sorting" onClick={() => handleSortingColum("state")}>
                    {translate("State")}
                  </th>
                )}

                {selectedColumns.timestamp && (
                  <th className="sorting" onClick={() => handleSortingColum("timestamp")}>
                    {translate("Date")}
                  </th>
                )}

                {selectedColumns.username && (
                  <th className="sorting" onClick={() => handleSortingColum("username")}>
                    {translate("User")}
                  </th>
                )}

                {selectedColumns.email && (
                  <th className="sorting" onClick={() => handleSortingColum("email")}>
                    {translate("Email")}
                  </th>
                )}

                <th>{translate("Action")}</th>
              </tr>
            </thead>

            <tbody className="ligth-body">
              {loading ? (
                <tr>
                  <td className="notification-loading-cell" colSpan={visibleColumnCount}>
                    <PropagateLoader color={"#123abc"} loading={loading} size={20} />
                  </td>
                </tr>
              ) : list_Notifications.length > 0 ? (
                list_Notifications.map((notification) => {
                  const target = getNotificationTarget(notification);
                  const details = parseNotificationMessage(notification.message);

                  return (
                  <tr
                    key={notification.id_notification}
                    className={`notification-row ${
                      notification.state === 1 ? "is-unread" : ""
                    }`}
                    onClick={() => handleOpenNotification(notification)}
                  >
                    <td>
                      <div className="form-check form-check-inline">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          onClick={(event) => event.stopPropagation()}
                        />
                      </div>
                    </td>

                    {selectedColumns.id_notification && (
                      <td>
                        <span className="notification-code">
                          #{notification.id_notification}
                        </span>
                      </td>
                    )}
                    {selectedColumns.type && (
                      <td>
                        <span className="notification-type">
                          {notification.type}
                        </span>
                      </td>
                    )}
                    {selectedColumns.message && (
                      <td className="notification-message-cell">
                        <div className="notification-summary-line">
                          <span className="notification-company">
                            {details.company}
                          </span>
                          {details.account && (
                            <span className="notification-account">
                              {details.account}
                            </span>
                          )}
                        </div>
                        <div className="notification-message">
                          {details.email || notification.message}
                        </div>
                        {target && (
                          <div className="notification-target">
                            {translate("go_to_profile")} {translate(target.labelKey)}
                            {" "}#{target.userId}
                          </div>
                        )}
                      </td>
                    )}
                    {selectedColumns.severity && (
                      <td className="notification-compact-cell">
                        {notification.severity}
                      </td>
                    )}

                    {selectedColumns.state && (
                      <td className="notification-compact-cell">
                        {notification.state === 1 ? (
                          <span className="badge badge-warning">{translate("Unread")}</span>
                        ) : (
                          <span className="badge badge-success">{translate("Read")}</span>
                        )}
                      </td>
                    )}

                    {selectedColumns.timestamp && (
                      <td className="notification-date-cell">
                        {toTimestamp(notification.timestamp)}
                      </td>
                    )}
                    {selectedColumns.username && (
                      <td className="notification-compact-cell">
                        {notification.username || "-"}
                      </td>
                    )}
                    {selectedColumns.email && <td>{notification.email || "-"}</td>}
                    <td
                      className="notification-action-cell"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        className="btn btn-sm btn-light notification-detail-btn"
                        title="Voir les details"
                        onClick={() => handleOpenNotification(notification)}
                      >
                        <i className="las la-eye"></i>
                      </button>
                      <Link
                        className="btn btn-sm btn-primary notification-open-btn"
                        to={getNotificationRoute(notification)}
                        state={{ canEditCompany: true }}
                        onClick={() => {
                          markNotificationAsRead(notification.id_notification);
                        }}
                      >
                        <i className="fas fa-external-link-alt"></i>
                      </Link>
                    </td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={visibleColumnCount}>
                    {translate("no_notification_available")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Offcanvas
        show={detailDrawerOpen}
        onHide={() => setDetailDrawerOpen(false)}
        placement="end"
        className="notification-detail-drawer"
      >
        <Offcanvas.Header closeButton className="notification-detail-header">
          <Offcanvas.Title>
            {selectedNotification
              ? `${translate("notification")} #${selectedNotification.id_notification}`
              : translate("notification")}
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="notification-detail-body">
          {selectedNotification && (() => {
            const details = parseNotificationMessage(selectedNotification.message);
            const target = getNotificationTarget(selectedNotification);

            return (
              <>
                <div className="notification-detail-top">
                  <span className="notification-detail-type">
                    {selectedNotification.type}
                  </span>
                  {selectedNotification.state === 1 ? (
                    <span className="badge badge-warning">{translate("Unread")}</span>
                  ) : (
                    <span className="badge badge-success">{translate("Read")}</span>
                  )}
                </div>

                <div className="notification-detail-title">
                  {details.company}
                </div>

                <div className="notification-detail-grid">
                  <div>
                    <span>{translate("Type")}</span>
                    <strong>{details.kind || selectedNotification.type}</strong>
                  </div>
                  <div>
                    <span>{translate("Date")}</span>
                    <strong>{toTimestamp(selectedNotification.timestamp)}</strong>
                  </div>
                  <div>
                    <span>{translate("User")}</span>
                    <strong>
                      {details.userId ? `#${details.userId}` : selectedNotification.username || "-"}
                    </strong>
                  </div>
                  <div>
                    <span>{translate("Severity")}</span>
                    <strong>{selectedNotification.severity}</strong>
                  </div>
                </div>

                {(details.account || details.email) && (
                  <div className="notification-detail-section">
                    <h6>Compte</h6>
                    {details.account && (
                      <div className="notification-detail-info">
                        <span>Username</span>
                        <strong>{details.account}</strong>
                      </div>
                    )}
                    {details.email && (
                      <div className="notification-detail-info">
                        <span>{translate("Email")}</span>
                        <strong>{details.email}</strong>
                      </div>
                    )}
                  </div>
                )}

                <div className="notification-detail-section">
                  <h6>{translate("Message")}</h6>
                  <p className="notification-detail-message">{details.raw}</p>
                </div>

                {target && (
                  <button
                    className="btn btn-primary btn-block notification-detail-primary"
                    onClick={() => handleGoToNotificationTarget(selectedNotification)}
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    {translate("go_to_profile")} {translate(target.labelKey)} #{target.userId}
                  </button>
                )}
              </>
            );
          })()}
        </Offcanvas.Body>
      </Offcanvas>

      <div className="row">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {translate("Displaying")} {list_Notifications.length} {translate("out of")} {total}
          </span>
        </div>

        <div className="col-md-6">
          <ReactPaginate
            previousLabel={translate("previous")}
            nextLabel={translate("next")}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
          />
        </div>
      </div>
    </>
  );
}
